import angular from 'angular';
import moment from 'moment';
import guid from '@/shared/utils/guid';
import { dynamicSort, dynamicSortMultiple } from '@/shared/utils/dynamicSort';

angular
  .module('cloudX')
  .controller('AddAssignmentDialogCtrl', [
    '$http',
    'appUrls',
    'flySvc',
    '$filter',
    '$q',
    '$element',
    'dataCache',
    'close',
    'relation',
    'invoiceStreams',
    addAssignmentDialogCtrl,
  ]);

function addAssignmentDialogCtrl(
  $http,
  appUrls,
  flySvc,
  $filter,
  $q,
  $element,
  dataCache,
  close,
  relation,
  invoiceStreams,
) {
  const vm = this;
  vm.saving = false;
  vm.relation = relation;
  vm.templates = null;
  vm.editName = false;
  vm.taskSchedules = [];
  vm.showScheduleDate = false;
  vm.invoiceStream = null; // Used to store the new invoiceStream after saving, in case something went wrong with saving the task schedules
  vm.templateRequired = !dataCache.getArray('assignmentsWithoutTemplate');
  vm.companies = dataCache.getCompanies().sort(dynamicSort('name'));
  vm.allowMultiple = false;
  vm.mergeableStreams = (invoiceStreams || []).filter(s => !s.mergeWithInvoiceStreamId);

  vm.schedules = [];
  vm.assignment = {
    relationId: relation.id,
    name: '',
    startDate: moment
      .utc()
      .startOf('day')
      .toDate(),
    deadline: null,
    templateId: null,
    companyId: null,
    priceGridId: null,
    mergeWithInvoiceStreamId: null,
    schedule: null,
    amount: 0,
    ledger: '',
    costLedger:'',
  };
  
  function showError(error) {
    flySvc.error(error.data.code, error.data.parameters);
    return '';
  }

  $q.all([
    $http
      .get(`${appUrls.api}/api/AssignmentTemplate/Get?relationType=${vm.relation.type}`)
      .then(({ data }) => data),
    $http.get(`${appUrls.tasks}/Api/AssignmentTemplate`).then(({ data = [] }) => data),
    $http
      .get(`${appUrls.tasks}/api/TaskTemplates/Schedules?relationType=${vm.relation.type}`)
      .then(({ data = [] }) => data),
    $http.get(`${appUrls.api}/api/invoiceschedule/get`).then(({ data = [] }) => data),
  ]).then(([assignments, taskConfig, taskTemplates, schedules]) => {
    vm.taskConfig = taskConfig || [];
    vm.taskTemplates = taskTemplates || [];
    vm.schedules = (schedules || [])
      .map(s => ({
        ...s,
        label: $filter('translateList')(s.id, 'invoiceSchedule', s.name),
      }))
      .sort(dynamicSort('label'));

    vm.templates = (assignments || [])
      .map(t => ({
        ...t,
        id: t.id,
        code: t.code || '',
        label: `${t.code ? `${t.code} – ` : ''}${$filter('translateList')(
          t.id,
          'assignmenttemplate',
          t.name,
        )}`,
        name: t.name,
      }))
      .sort(dynamicSortMultiple('code', 'label'));
    if (!vm.templateRequired) {
      vm.templates.unshift({
        id: guid.empty,
        code: null,
        label: $filter('translate')('Do not use a template'),
        name: '',
      });
    }
  }, showError);

  vm.templateChanged = () => {
    const template = vm.assignment.templateId
      ? vm.templates.find(t => t.id === vm.assignment.templateId)
      : null;
    vm.assignment.name = (template && template.name) || '';
    vm.allowMultiple = template?.allowMultiple ?? false;
    if (vm.allowMultiple) {
      vm.assignment.name = $filter('translateList')(
        template.id,
        'assignmenttemplate',
        template.name,
        undefined,
        relation.languageCode
      );
    }
    if (!template?.mergeWithAssignmentTemplateId) {
      vm.assignment.mergeWithInvoiceStreamId = null;
    } else if (template.mergeWithAssignmentTemplateId === guid.empty) {
      vm.assignment.mergeWithInvoiceStreamId = vm.mergeableStreams.find(
        x => !x.endDate && x.isMain
      )?.id;
    } else {
      vm.assignment.mergeWithInvoiceStreamId = vm.mergeableStreams.find(
        x => !x.endDate && x.assignmentTemplateId === template.mergeWithAssignmentTemplateId
      )?.id;
    }
    vm.editName = vm.allowMultiple || (!vm.templateRequired && !vm.assignment.name);
    vm.assignment.schedule =
      template && template.invoiceScheduleId
        ? vm.schedules.find(x => x.id === template.invoiceScheduleId) || null
        : null;
    vm.assignment.amount = (template && template.amount) ?? 0;
    vm.assignment.ledger = (template && template.ledger) || '';
    vm.assignment.costLedger = (template && template.costLedger) || '';
    vm.assignment.companyId = (template && template.companyId) ?? vm.assignment.companyId;
    if (vm.assignment.templateId) {
      vm.taskSchedules = vm.taskConfig
        .filter(x => x.assignmentTemplateId === vm.assignment.templateId)
        .map(c => {
          const taskTemplate = vm.taskTemplates.find(x => x.id === c.taskTemplateId);
          if (!taskTemplate) return null;
          if (!taskTemplate.periodicities || !taskTemplate.periodicities.length) return null;
          let periodicity = c.taskPeriodicityId
            ? taskTemplate.periodicities.find(x => x.id === c.taskPeriodicityId)
            : null;
          if (!periodicity && taskTemplate.periodicities.length === 1)
            periodicity = taskTemplate.periodicities[0];

          return {
            invoiceSteamId: null, // Will be filled in after save
            relationId: vm.relation.id,
            type: 1,
            template: {
              id: taskTemplate.id,
              name: taskTemplate.name,
            },
            periodicities: taskTemplate.periodicities || [],
            periodicity,
            planningStart: new Date(),
            planningStop: null,
            scheduleDate: null,
            removed: false,
          };
        })
        .filter(x => !!x); // Filter out nulls
    } else {
      vm.taskSchedules = [];
    }
    vm.resetShowScheduleDate();
  };
  vm.periodicityChanged = (item) => {
    if (item.periodicity && item.periodicity.showScheduleDate) {
      vm.showScheduleDate = true;
    } else {
      item.scheduleDate = null;
      vm.resetShowScheduleDate();
    }
  };
  vm.resetShowScheduleDate = () => {
    vm.showScheduleDate = vm.taskSchedules.some(
      x => x.periodicity && x.periodicity.showScheduleDate
    );
  };

  vm.saveTaskSchedules = function(invoiceStreamId) {
    const schedules = (vm.taskSchedules || [])
      .filter(x => !x.removed)
      .map(x => {
        const model = angular.copy(x);
        model.invoiceStreamId = invoiceStreamId;

        if (model.planningStart instanceof Date)
          model.planningStart = moment.utc(model.planningStart);
        if (model.planningStop && model.planningStop instanceof Date)
          model.planningStop = moment.utc(model.planningStop);

        if (model.scheduleDate && model.scheduleDate instanceof Date)
          model.scheduleDate = moment.utc(model.scheduleDate);
        delete model.periodicities;
        delete model.removed;

        return model;
      });
    if (!schedules.length) return $q.when([]);
    return $http
      .post(`${appUrls.tasks  }/Api/TaskSchedules/Create`, schedules)
      .then(({ data: taskSchedules }) => {
        const withErrors = taskSchedules
          .filter(x => !!x.planningError && !!x.template)
          .map(x => {
            const taskName = $filter('translateList')(
              x.template.id,
              'tasktemplate',
              x.template.name
            );

            const error = $filter('translateList')(
              `${x.planningError}`,
              'TaskDomainExceptionCode',
              ''
            );
            return `<b>${taskName}:</b> ${error}`;
          });
        if (withErrors.length > 0) {
          flySvc.custom({
            toastrType: 'warning',
            titleMessage: 'msgSomeTaskSchedulesCouldNotBePlanned',
            content: withErrors.join(' <br />\r\n'),
          });
        }

        return taskSchedules;
      });
  };

  vm.save = () => {
    if (vm.saving) return '';
    vm.saving = true;
    if (vm.invoiceStream && vm.invoiceStream.id) {
      return vm
        .saveTaskSchedules(vm.invoiceStream.id)
        .then(
          schedules => {
            close(
              {
                invoiceStream: vm.invoiceStream,
                hasTaskSchedules: schedules && schedules.length > 0,
              },
              500
            );
            $element.modal('hide');
          },
          error => {
            showError(error);
          }
        )
        .finally(() => {
          vm.saving = false;
        });
    } 
      const model = angular.copy(vm.assignment);
      const invoicingType = vm.assignment.schedule ? vm.assignment.schedule.invoicingType : null;
      model.invoiceScheduleId = vm.assignment.schedule ? vm.assignment.schedule.id : null;
      model.amount = vm.assignment.schedule && invoicingType !== 0 ? vm.assignment.amount : null;
      model.ledger = vm.assignment.schedule ? vm.assignment.ledger || null : null;
      model.costLedger = vm.assignment.schedule ? vm.assignment.costLedger || null : null;
      delete model.schedule;
      $http
        .post(`${appUrls.api  }/api/Assignment/Create`, model)
        .then(({ data: invoiceStream }) => {
          if (invoiceStream) {
            vm.invoiceStream = invoiceStream;

            return vm.saveTaskSchedules(invoiceStream.id).then(schedules => {
              close(
                {
                  invoiceStream: vm.invoiceStream,
                  hasTaskSchedules: schedules && schedules.length > 0,
                },
                500
              );
              $element.modal('hide');
            });
          }
          vm.saving = false;
          return false;
        })
        .catch(error => {
          showError(error);
        })
        .finally(() => {
          vm.saving = false;
        });
    
  };
  vm.cancel = () => {
    close(null, 500);
  };
  vm.dayMonthOptions = {
    minMode: 'day',
    maxMode: 'month',
    showWeeks: false,
    formatDayTitle: 'MMMM',
    datepickerMode: 'month',
    formatMonthTitle: ' ',
    initDate: moment
      .utc()
      .startOf('day')
      .set({
        year: 2000,
      })
      .toDate(),
    // minDate: new Date(2000, 0, 1),
    // maxDate: new Date(2000, 11, 31),
  };
}
